import React, { Suspense, lazy, useEffect } from "react"
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom"
import { Provider, useSelector } from "react-redux"
import store from "../redux/store"
import { routeMap } from "./routeMap"
// PrivateRoute stays eager — it's the route guard, always needed
import PrivateRoute from "../components/Commons/PrivateRoute"
import { useCanExport } from "../hooks/useCanExport"

import { DemoRoute } from "../demo/demoExports"

// Lazy-loaded page components — each creates a separate webpack chunk
// loaded on first navigation to that route (~40-60% initial bundle reduction)
// NOTE: SA facility Admin pages (FacilityPageTable, WorkSitesList, FacilityDetail,
// AddFacilityPage, EditFacilityPage) are intentionally not imported/routed here —
// the SA Admin tab was removed. Page files are kept for now; see FA my-facility.
const AddUserPage = lazy(
  () => import("../pages/SuperAdmin/Facility/AddUserPage"),
)
const EditUserPage = lazy(
  () => import("../pages/SuperAdmin/Facility/EditUserPage"),
)
const ViewUserPage = lazy(
  () => import("../pages/SuperAdmin/Facility/ViewUserPage"),
)
const SchedulingProfile = lazy(
  () => import("../pages/SuperAdmin/Scheduling/SchedulingProfile"),
)
const ScProfile = lazy(() => import("../pages/SuperAdmin/Scheduling/ScProfile"))
const ScCredentialing = lazy(
  () => import("../pages/SuperAdmin/Scheduling/ScCredentialing"),
)
const Shifts = lazy(() => import("../pages/SuperAdmin/Scheduling/Shifts"))
const Calendar = lazy(() => import("../pages/SuperAdmin/Scheduling/Calendar"))
const Documents = lazy(() => import("../pages/SuperAdmin/Scheduling/Documents"))
const DocumentsTable = lazy(
  () => import("../pages/SuperAdmin/Scheduling/DocumentTable"),
)
const SpendManagementProfile = lazy(
  () => import("../pages/SuperAdmin/SpendManagement/SpendManagementProfile"),
)
const SpendProfile = lazy(
  () => import("../pages/SuperAdmin/SpendManagement/SpendProfile"),
)
const TimeEntries = lazy(
  () => import("../pages/SuperAdmin/SpendManagement/TimeEntries"),
)
const SpendDocuments = lazy(
  () => import("../pages/SuperAdmin/SpendManagement/SpendDocuments"),
)
const SpInvoiceProfile = lazy(
  () => import("../pages/SuperAdmin/SpendManagement/SpInvoiceProfile"),
)
const InvProfile = lazy(
  () => import("../pages/SuperAdmin/SpendManagement/InvProfile"),
)
const InvTimeEntries = lazy(
  () => import("../pages/SuperAdmin/SpendManagement/InvTimeEntries"),
)
const InvExpenses = lazy(
  () => import("../pages/SuperAdmin/SpendManagement/InvExpenses"),
)
const SpendMtgInvoicesMain = lazy(
  () => import("../pages/SuperAdmin/SpendManagement/SpendMtgInvoicesMain"),
)
const SpendMgtTimesheetMain = lazy(
  () => import("../pages/SuperAdmin/SpendManagement/SpendMgtTimesheetMain"),
)
const Timesheet = lazy(() => import("../pages/SuperAdmin/Scheduling/Timesheet"))
const Invoice = lazy(() => import("../pages/SuperAdmin/Scheduling/Invoice"))
const MyProfile = lazy(() => import("../pages/SuperAdmin/MyProfile"))
// NOTE: SA dashboard CRUD pages (AddReport, EditReport) and the show-iframe
// viewer (TableauPage) are intentionally not imported/routed here -- dashboard
// URL management moved to Admin Portal and the SA Dashboards tab is now a
// read-only viewer. Page files are kept for now; see e5980e5a precedent.
const MyFacility = lazy(() => import("../pages/SuperAdmin/Facility/MyFacility"))
const InvDocuments = lazy(
  () => import("../pages/SuperAdmin/SpendManagement/InvDocuments"),
)
const KnowledgeBasePage = lazy(
  () => import("../pages/KnowledgeBase/KnowledgeBasePage"),
)
const DesignSystem = lazy(
  () => import("../pages/SuperAdmin/DevTools/DesignSystem"),
)
const SuperAdminHome = lazy(
  () => import("../pages/SuperAdmin/Home/SuperAdminHome"),
)

// Syncs Redux theme state to:
//   1. data-theme attribute on <html> (drives CSS custom-property overrides)
//   2. .dark class on <html> (used by Tailwind / utility dark: classes)
// Must live INSIDE <Provider> so useSelector is available.
function ThemeWrapper({ children }) {
  const mode = useSelector((state) => state.theme?.mode || "light")

  // Sync data-theme attribute (drives all dark mode CSS via [data-theme="dark"] selectors)
  useEffect(() => {
    if (mode === "light") {
      delete document.documentElement.dataset.theme
    } else {
      document.documentElement.dataset.theme = mode
    }
  }, [mode])

  return <>{children}</>
}

// Mirrors canExport onto <body data-no-export> so the print stylesheet can
// blank the page for a Read-Only Admin (best effort). Must live INSIDE <Provider>.
export function ExportGuard() {
  const canExport = useCanExport()
  useEffect(() => {
    if (canExport) {
      delete document.body.dataset.noExport
    } else {
      document.body.dataset.noExport = "true"
    }
    return () => {
      delete document.body.dataset.noExport
    }
  }, [canExport])
  return null
}

const LandingPage = lazy(() => import("../pages/SignIn/LandingPage"))
const HomePage = lazy(() => import("../pages/HomePage"))
const Facility = lazy(() => import("../pages/SuperAdmin/Facility"))
const DashboardPage = lazy(() => import("../pages/SuperAdmin/DashboardPage"))
const Candidate = lazy(
  () => import("../pages/SuperAdmin/Dashboard/Candidate/Candidate"),
)
const SchedulingDashboard = lazy(
  () => import("../pages/SuperAdmin/Dashboard/Scheduling/Scheduling"),
)
const SpendManagementDashboard = lazy(
  () => import("../pages/SuperAdmin/Dashboard/SpendManagement/SpendManagement"),
)
const ScheduledReports = lazy(
  () =>
    import("../pages/SuperAdmin/Dashboard/ScheduledReports/ScheduledReports"),
)
const LandingPageDashboard = lazy(
  () =>
    import(
      "../pages/SuperAdmin/Dashboard/LandingPageDashboard/LandingPageDashboard"
    ),
)
const LocumsAnalytics = lazy(
  () => import("../pages/SuperAdmin/Dashboard/LocumsAnalytics/LocumsAnalytics"),
)
const CandidateMain = lazy(
  () => import("../pages/SuperAdmin/Dashboard/Candidate/CandidateMain"),
)

const SchedulingMain = lazy(
  () => import("../pages/SuperAdmin/Dashboard/Scheduling/SchedulingMain"),
)

const SpendManagementMain = lazy(
  () =>
    import("../pages/SuperAdmin/Dashboard/SpendManagement/SpendManagementMain"),
)

const LocumsAnalyticsMain = lazy(
  () =>
    import("../pages/SuperAdmin/Dashboard/LocumsAnalytics/LocumsAnalyticsMain"),
)

const LandingPageDashboardMain = lazy(
  () =>
    import(
      "../pages/SuperAdmin/Dashboard/LandingPageDashboard/LandingPageDashboardMain"
    ),
)

const ScheduledReportsMain = lazy(
  () =>
    import(
      "../pages/SuperAdmin/Dashboard/ScheduledReports/ScheduledReportsMain"
    ),
)

const ScheduleManagement = lazy(
  () => import("../pages/SuperAdmin/ScheduleManagement"),
)
const SchedulingConfirmations = lazy(
  () => import("../pages/SuperAdmin/Scheduling/SchedulingConfirmations"),
)
const MasterCalendar = lazy(
  () => import("../pages/SuperAdmin/Scheduling/MasterCalendar"),
)
const SpendMtgTimesheets = lazy(
  () => import("../pages/SuperAdmin/SpendManagement/SpendMtgTimesheets"),
)
const SpendMtgInvoices = lazy(
  () => import("../pages/SuperAdmin/SpendManagement/SpendMtgInvoices"),
)
const SpendManagement = lazy(
  () => import("../pages/SuperAdmin/SpendManagement"),
)

const NotFound = lazy(() => import("../pages/NotFound"))

// Re-export from dedicated file to avoid circular dependency
// (Routes → PrivateRoute → headerMenu → Routes)
export { routeMap } from "./routeMap"

const RoutesConfig = () => {
  return (
    <Provider store={store}>
      <ThemeWrapper>
        <ExportGuard />
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Suspense
            fallback={
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100vh",
                }}
              >
                <div
                  className="p-progress-spinner"
                  style={{
                    width: 50,
                    height: 50,
                    border: "3px solid var(--border-subtle-2, #e2e8f0)",
                    borderTopColor: "var(--primary_color, #22c55e)",
                    borderRadius: "50%",
                    animation: "spin 0.8s linear infinite",
                  }}
                />
              </div>
            }
          >
            <Routes>
              <Route path={routeMap.LANDING_PAGE} element={<LandingPage />} />
              {/* Optional param so /knowledge and /knowledge/:slug share one
                  mounted component (the article tree doesn't reload when
                  opening/closing an article) while multi-segment junk still
                  falls through to the NotFound catch-all. */}
              <Route path="/knowledge/:slug?" element={<KnowledgeBasePage />} />
              <Route
                path="/email-verification-success"
                element={<Navigate to="/home" replace />}
              />
              <Route
                path="/email-confirm"
                element={<Navigate to="/home" replace />}
              />
              {DemoRoute}
              <Route
                path="/home/*"
                element={
                  <PrivateRoute>
                    <HomePage />
                  </PrivateRoute>
                }
              >
                <Route
                  path="profile"
                  element={
                    <PrivateRoute>
                      <MyProfile />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="knowledge/:slug?"
                  element={
                    <PrivateRoute>
                      <KnowledgeBasePage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="dev/design-system"
                  element={
                    <PrivateRoute allowedRoles={["Super Admin"]}>
                      <DesignSystem />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="overview"
                  element={
                    <PrivateRoute allowedRoles={["Super Admin"]}>
                      <SuperAdminHome />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="dashboard/*"
                  element={
                    <PrivateRoute>
                      <DashboardPage />
                    </PrivateRoute>
                  }
                >
                  <Route index element={<Navigate to="candidates" replace />} />
                  <Route
                    path="candidates"
                    element={
                      <PrivateRoute>
                        <Candidate />
                      </PrivateRoute>
                    }
                  >
                    <Route
                      path="*"
                      element={
                        <PrivateRoute>
                          <CandidateMain />
                        </PrivateRoute>
                      }
                    />
                  </Route>
                  <Route
                    path="landing-page"
                    element={
                      <PrivateRoute>
                        <LandingPageDashboard />
                      </PrivateRoute>
                    }
                  >
                    <Route
                      path="*"
                      element={
                        <PrivateRoute>
                          <LandingPageDashboardMain />
                        </PrivateRoute>
                      }
                    />
                  </Route>

                  <Route
                    path="scheduling"
                    element={
                      <PrivateRoute>
                        <SchedulingDashboard />
                      </PrivateRoute>
                    }
                  >
                    <Route
                      path="*"
                      element={
                        <PrivateRoute>
                          <SchedulingMain />
                        </PrivateRoute>
                      }
                    />
                  </Route>
                  <Route
                    path="spend-management"
                    element={
                      <PrivateRoute>
                        <SpendManagementDashboard />
                      </PrivateRoute>
                    }
                  >
                    <Route
                      path="*"
                      element={
                        <PrivateRoute>
                          <SpendManagementMain />
                        </PrivateRoute>
                      }
                    />
                  </Route>
                  <Route
                    path="locums-analytics"
                    element={
                      <PrivateRoute>
                        <LocumsAnalytics />
                      </PrivateRoute>
                    }
                  >
                    <Route
                      path="*"
                      element={
                        <PrivateRoute>
                          <LocumsAnalyticsMain />
                        </PrivateRoute>
                      }
                    />
                  </Route>
                  <Route
                    path="scheduled-reports"
                    element={
                      <PrivateRoute>
                        <ScheduledReports />
                      </PrivateRoute>
                    }
                  >
                    <Route
                      path="*"
                      element={
                        <PrivateRoute>
                          <ScheduledReportsMain />
                        </PrivateRoute>
                      }
                    />
                  </Route>
                </Route>
                <Route
                  path="schedule-management/*"
                  element={
                    <PrivateRoute
                      allowedRoles={[
                        "Super Admin",
                        "Facility Admin",
                        "Facility User",
                      ]}
                      requiredPermissions={["schedule_management"]}
                    >
                      <ScheduleManagement />
                    </PrivateRoute>
                  }
                >
                  <Route
                    path="*"
                    element={
                      <PrivateRoute>
                        <SchedulingConfirmations />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="master-calendar"
                    element={
                      <PrivateRoute>
                        <MasterCalendar />
                      </PrivateRoute>
                    }
                  />
                  {/* Deep link for facility confirmation letter email: /home/schedule-management/scheduling-profile?confirmationNumber={confirmationNumber} */}
                  <Route
                    path="scheduling-profile"
                    element={
                      <PrivateRoute>
                        <SchedulingProfile />
                      </PrivateRoute>
                    }
                  >
                    <Route
                      path="*"
                      element={
                        <PrivateRoute>
                          <ScProfile />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="shifts"
                      element={
                        <PrivateRoute>
                          <Shifts />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="calendar"
                      element={
                        <PrivateRoute>
                          <Calendar />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="credentialing"
                      element={
                        <PrivateRoute>
                          <ScCredentialing />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="timesheet"
                      element={
                        <PrivateRoute>
                          <Timesheet />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="invoice"
                      element={
                        <PrivateRoute>
                          <Invoice />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="documents/*"
                      element={
                        <PrivateRoute>
                          <Documents />
                        </PrivateRoute>
                      }
                    >
                      <Route
                        path="*"
                        element={
                          <PrivateRoute>
                            <DocumentsTable />
                          </PrivateRoute>
                        }
                      ></Route>
                    </Route>
                  </Route>
                </Route>
                <Route
                  path="spend-management/*"
                  element={
                    <PrivateRoute
                      allowedRoles={[
                        "Super Admin",
                        "Facility Admin",
                        "Facility User",
                      ]}
                      requiredPermissions={["spend_management_menu"]}
                    >
                      <SpendManagement />
                    </PrivateRoute>
                  }
                >
                  <Route
                    path="*"
                    element={
                      <PrivateRoute>
                        <SpendMtgTimesheets />
                      </PrivateRoute>
                    }
                  >
                    <Route
                      path="*"
                      element={
                        <PrivateRoute>
                          <SpendMgtTimesheetMain />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="spendManagement-profile"
                      element={
                        <PrivateRoute>
                          <SpendManagementProfile />
                        </PrivateRoute>
                      }
                    >
                      <Route
                        path="*"
                        element={
                          <PrivateRoute>
                            <SpendProfile />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="time-entries"
                        element={
                          <PrivateRoute>
                            <TimeEntries />
                          </PrivateRoute>
                        }
                      />

                      <Route
                        path="documents"
                        element={
                          <PrivateRoute>
                            <SpendDocuments />
                          </PrivateRoute>
                        }
                      />
                    </Route>
                  </Route>

                  <Route
                    path="spend-mtg-invoices"
                    element={
                      <PrivateRoute>
                        <SpendMtgInvoices />
                      </PrivateRoute>
                    }
                  >
                    <Route
                      path="*"
                      element={
                        <PrivateRoute>
                          <SpendMtgInvoicesMain />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="spend-invoice-profile"
                      element={
                        <PrivateRoute>
                          <SpInvoiceProfile />
                        </PrivateRoute>
                      }
                    >
                      <Route
                        path="*"
                        element={
                          <PrivateRoute>
                            <InvProfile />
                          </PrivateRoute>
                        }
                      />

                      <Route
                        path="expenses"
                        element={
                          <PrivateRoute>
                            <InvExpenses />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="time-entries"
                        element={
                          <PrivateRoute>
                            <InvTimeEntries />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="documents"
                        element={
                          <PrivateRoute>
                            <InvDocuments />
                          </PrivateRoute>
                        }
                      />
                    </Route>
                  </Route>
                </Route>
                <Route
                  path="my-facility"
                  element={
                    <PrivateRoute allowedRoles={["Facility Admin"]}>
                      <MyFacility />
                    </PrivateRoute>
                  }
                />
                <Route
                  path=""
                  element={
                    <PrivateRoute>
                      <Facility />
                    </PrivateRoute>
                  }
                >
                  {/* SA facility Admin routes (index, worksite-list, facility-detail,
                      add-facility, edit-facility) removed — SA Admin tab retired.
                      FA/FU user routes below are unchanged. */}
                  <Route
                    path="add-user"
                    element={
                      <PrivateRoute
                        allowedRoles={["Super Admin", "Facility Admin"]}
                      >
                        <AddUserPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="edit-user"
                    element={
                      <PrivateRoute
                        allowedRoles={["Super Admin", "Facility Admin"]}
                      >
                        <EditUserPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="view-user"
                    element={
                      <PrivateRoute
                        allowedRoles={["Super Admin", "Facility Admin"]}
                      >
                        <ViewUserPage />
                      </PrivateRoute>
                    }
                  />
                </Route>
                <Route
                  path="*"
                  element={
                    <PrivateRoute>
                      <NotFound />
                    </PrivateRoute>
                  }
                />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </Router>
      </ThemeWrapper>
    </Provider>
  )
}

export default RoutesConfig
